import { t } from "../lib/i18n";
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import EditCompModal from './EditCompModal';
import { addCompsChangeListener, removeCompsChangeListener, dispatchCompsChangeEvent } from '../compsEventSystem';
import { showConfirm, showAlert } from '../utils/ModalService';


interface MarketComp {
    id?: string;
    address: string;
    sale_date: string;
    sale_price: number;
    price_per_sqft: number;
    sqft: number;
}

interface MarketCompsProps {
    propertyId: string;
}

const MarketComps: React.FC<MarketCompsProps> = ({ propertyId }) => {
    const [comps, setComps] = useState<MarketComp[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newComp, setNewComp] = useState<Partial<MarketComp>>({});
    const [message, setMessage] = useState('');
    const [editingComp, setEditingComp] = useState<MarketComp | null>(null);

    const fetchComps = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/properties/${propertyId}/comps`);
            setComps(response.data);
        } catch (err) {
            console.error('Error fetching market comps:', err);
            setError('Failed to load market comparables.');
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        if (!propertyId) return;
        fetchComps();
        addCompsChangeListener(fetchComps);
        return () => {
            removeCompsChangeListener(fetchComps);
        };
    }, [propertyId, fetchComps]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewComp(prev => ({ ...prev, [name]: name === 'sale_price' || name === 'sqft' ? parseFloat(value) || 0 : value }));
    };

    const handleAddComp = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage('');
        try {
            const compDataToSend = {
                address: newComp.address,
                sale_date: newComp.sale_date,
                sale_price: newComp.sale_price,
                sqft: newComp.sqft,
            };
            await api.post(`/properties/${propertyId}/comps`, compDataToSend);
            setMessage('Comparable added successfully!');
            setNewComp({});
            fetchComps(); // Explicitly re-fetch comps
            dispatchCompsChangeEvent();
        } catch (error: any) {
            console.error('Error adding comparable:', error.response?.data || error.message);
            setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error adding comparable.');
        }
    };

    const handleEditComp = (comp: MarketComp) => {
        setEditingComp(comp);
    };

    const handleDeleteComp = async (compId: string) => {
        showConfirm('Confirm Action', 'Are you sure you want to delete this comparable sale?', async () => {
            try {
                await api.delete(`/comps/${compId}`);
                setMessage('Comparable sale deleted successfully!');
                dispatchCompsChangeEvent();
            } catch (error: any) {
                console.error('Error deleting comparable sale:', error);
                setMessage(error.response?.data?.detail || 'Failed to delete comparable sale.');
            }
        });
    };

    const handleCloseModal = () => {
        setEditingComp(null);
    };

    if (loading) {
        return <div>{t("marketComps.k1")}</div>;
    }

    if (error) {
        return <div className="text-danger">{t("marketComps.k2")} {error}</div>;
    }

    const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

    return (
        <div className="mt-8">
            <EditCompModal comp={editingComp} onClose={handleCloseModal} />
            <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("marketComps.k3")}</h2>
            {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}
            <form onSubmit={handleAddComp} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
                <input type="text" name="address" placeholder={t("marketComps.k4")} value={newComp.address || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
                <div>
                    <label htmlFor="sale_date" className="block text-sm font-medium text-text-secondary mb-1">{t("marketComps.k5")}</label>
                    <input type="date" id="sale_date" name="sale_date" value={newComp.sale_date || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
                </div>
                <input type="number" name="sale_price" placeholder={t("marketComps.k6")} value={newComp.sale_price || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
                <input type="number" name="sqft" placeholder={t("marketComps.k7")} value={newComp.sqft || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
                <button type="submit" className="col-span-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">{t("marketComps.k8")}</button>
            </form>

            <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("marketComps.k9")}</h2>
            <div className="overflow-x-auto bg-bg-secondary p-4 rounded-lg shadow border border-border-primary">
                {comps.length === 0 ? (
                    <p className="text-text-secondary">{t("marketComps.k10")}</p>
                ) : (
                    <table className="min-w-full divide-y divide-border-primary">
                        <thead className="bg-bg-tertiary">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("marketComps.k4")}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("marketComps.k5")}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("marketComps.k6")}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("marketComps.k11")}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("marketComps.k12")}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-bg-secondary divide-y divide-border-primary">
                            {comps.map((comp) => (
                                <tr key={comp.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">{comp.address}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{comp.sale_date}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{formatCurrency(comp.sale_price)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{formatCurrency(comp.price_per_sqft)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button onClick={() => handleEditComp(comp)} className="text-indigo-600 hover:text-indigo-900 mr-4">{t("marketComps.k13")}</button>
                                        <button onClick={() => handleDeleteComp(comp.id!)} className="text-red-600 hover:text-red-900">{t("marketComps.k14")}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default MarketComps;
